import { useState, useEffect } from 'react'
import api from '../services/api'

// Tabela Categorias Profissionais (TMOTCAT, frota.motcat) — cargos do funcionário

interface Categoria { catcod: string; catdes: string; catcat: string; catint: string; usos?: number }

// valores de catcat existentes no banco; só "Motorista" (MM) confirmado na tela do ERP, os demais seguem a Ficha Ponto
const CATEGORIAS: [string, string][] = [
  ['MM', 'Motorista'], ['AD', 'Administrativo'], ['MC', 'Mecânica'], ['GE', 'Gerência'], ['OT', 'Outros'], ['AA', 'AA'],
]
const nomeCategoria = (v: string) => CATEGORIAS.find(([c]) => c === v)?.[1] ?? v

function Lista({ lista, onAdicionar, onEditar, onExcluir }: {
  lista: Categoria[]
  onAdicionar: () => void
  onEditar: (c: Categoria) => void
  onExcluir: (c: Categoria) => void
}) {
  const [busca, setBusca] = useState('')
  const t = busca.trim().toUpperCase()
  const filtrada = t ? lista.filter(c => c.catcod.includes(t) || c.catdes.includes(t)) : lista
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Categorias cadastradas ({lista.length})</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="field" style={{ margin: 0 }}>
            <input value={busca} placeholder="Buscar código ou descrição" style={{ width: 240 }} onChange={e => setBusca(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={onAdicionar}>+ Adicionar</button>
        </div>
      </div>

      {filtrada.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhuma categoria encontrada.</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filtrada.map(c => (
          <div key={c.catcod} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{c.catcod} - {c.catdes}</div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>Categoria: <b>{nomeCategoria(c.catcat) || '—'}</b></span>
                <span>Integra Sintra: <b>{c.catint === 'S' ? 'Sim' : 'Não'}</b></span>
                <span>Funcionários/histórico: <b>{c.usos ?? 0}</b></span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => onEditar(c)}>Editar</button>
              <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }} onClick={() => onExcluir(c)}
                disabled={(c.usos ?? 0) > 0} title={(c.usos ?? 0) > 0 ? 'Categoria usada em funcionários' : undefined}>Excluir</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Formulario({ editando, inicial, onVoltar, onSalvo }: {
  editando: boolean
  inicial: Categoria
  onVoltar: () => void
  onSalvo: () => void
}) {
  const [form, setForm] = useState<Categoria>(inicial)
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)
  const opcoes = !form.catcat || CATEGORIAS.some(([c]) => c === form.catcat) ? CATEGORIAS : [...CATEGORIAS, [form.catcat, form.catcat] as [string, string]]

  async function salvar() {
    if (!form.catcod.trim()) { setErro('Código inválido!'); return }
    if (!form.catdes.trim()) { setErro('Descrição inválida!'); return }
    setSalvando(true); setErro('')
    try {
      if (editando) await api.put(`/categorias-profissionais/${encodeURIComponent(form.catcod)}`, form)
      else await api.post('/categorias-profissionais', form)
      onSalvo()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>{editando ? 'Editar Categoria' : 'Nova Categoria'}</h3>
        <button className="btn btn-outline" onClick={onVoltar}>Voltar</button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field">
          <label>Código *</label>
          <input value={form.catcod} maxLength={2} disabled={editando} style={{ width: '6ch' }}
            onChange={e => setForm(f => ({ ...f, catcod: e.target.value.toUpperCase() }))} />
        </div>
        <div className="field">
          <label>Categoria</label>
          <select value={form.catcat} onChange={e => setForm(f => ({ ...f, catcat: e.target.value }))}>
            {opcoes.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Descrição *</label>
          <input value={form.catdes} maxLength={25} style={{ width: '27ch' }}
            onChange={e => setForm(f => ({ ...f, catdes: e.target.value.toUpperCase() }))} />
        </div>
        <div className="field">
          <label>Integra Sintra</label>
          <select value={form.catint} onChange={e => setForm(f => ({ ...f, catint: e.target.value }))}>
            <option value="N">Não</option>
            <option value="S">Sim</option>
          </select>
        </div>
      </div>

      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.75rem' }}>{erro}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Adicionar'}
        </button>
        <button className="btn btn-outline" onClick={onVoltar}>Cancelar</button>
      </div>
    </div>
  )
}

export default function CategoriasProfissionais() {
  const [lista, setLista] = useState<Categoria[]>([])
  const [carregado, setCarregado] = useState(false)
  const [atual, setAtual] = useState<Categoria | null>(null)
  const [editando, setEditando] = useState(false)
  const [erro, setErro] = useState('')

  async function carregar() {
    const r = await api.get('/categorias-profissionais')
    setLista(r.data)
    setCarregado(true)
  }

  useEffect(() => { carregar().catch(() => setErro('Erro ao carregar as categorias.')) }, [])

  async function excluir(c: Categoria) {
    if (!window.confirm(`Excluir a categoria "${c.catcod} - ${c.catdes}"?`)) return
    setErro('')
    try {
      await api.delete(`/categorias-profissionais/${encodeURIComponent(c.catcod)}`)
      await carregar()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao excluir.')
    }
  }

  return (
    <div style={{ maxWidth: 860 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Categorias Profissionais
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="Tabela frota.motcat">TMOTCAT</span>
      </h2>
      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{erro}</div>}

      {!carregado ? null : atual ? (
        <Formulario key={atual.catcod || 'nova'} editando={editando} inicial={atual}
          onVoltar={() => setAtual(null)} onSalvo={async () => { await carregar(); setAtual(null) }} />
      ) : (
        <Lista lista={lista}
          onAdicionar={() => { setAtual({ catcod: '', catdes: '', catcat: 'MM', catint: 'N' }); setEditando(false) }}
          onEditar={c => { setAtual({ ...c }); setEditando(true) }}
          onExcluir={excluir} />
      )}
    </div>
  )
}
